// Refusal codes with no phrase of their own; internal/api/phrases_test.go reads this file.

/**
 * Codes a person can only meet because this screen sent something malformed. One
 * sentence covers all of them: the reader can do nothing about the difference, and
 * support can, from the correlation id.
 */
export const CLIENT_FAULTS: readonly string[] = [
  'MALFORMED_JSON', 'EMPTY_BODY', 'BODY_TOO_LARGE', 'UNSUPPORTED_MEDIA_TYPE', 'JSON_REQUIRED',
  'IDEMPOTENCY_KEY_REQUIRED', 'IDEMPOTENCY_KEY_TOO_LONG', 'IDEMPOTENCY_KEY_REUSED',
  'UNKNOWN_ACTION', 'MISSING_BRANCH',
  // A path or method no route takes: a bug in the calling code.
  'NO_SUCH_ROUTE',
  // A machine's log line at a level the host API does not name: a bug in the screen.
  'BAD_LEVEL',
]

/**
 * Codes deliberately left in the server's English, and why. The Go check in
 * internal/api/phrases_test.go reads this file: a code the server can send must be
 * given words above, or be listed in one of these two lists.
 */
export const ENGLISH_ONLY: readonly string[] = [
  // Answered to a machine's screen, which reads kioskText, not a person's.
  'NOT_A_KIOSK', 'NO_SUCH_SCREEN', 'RETIRED', 'BRANCH_RETIRED', 'BRANCH_CLOSED', 'TOO_MANY_TICKETS',
  // Part H: the kiosk's booking keypad, which says it in the customer's language
  // from kioskText (arrivalRefusal in lib/kioskBooking.ts).
  'ARRIVE_TOO_MANY',
  // Part K: answered to the feedback tablet, which reads kioskText, not a person's.
  'NOT_A_FEEDBACK_TABLET', 'FEEDBACK_NOT_FROM_TABLET', 'FEEDBACK_CLOSED', 'TOO_MANY_PRESSES', 'FEEDBACK_TOO_OFTEN',
  // Answered to a counter display asking about its desk's visit; it keeps what it shows.
  'NOT_A_DESK_DISPLAY', 'DESK_TOO_OFTEN',
  // Administration sentences that carry names or lists the server did not send
  // separately; precise in English, and read by the person setting the branch up.
  'QUEUE_CODE_TAKEN', 'COUNTER_CODE_TAKEN', 'FIELD_IN_USE', 'FIELD_KEY_TAKEN',
  'SERVICE_ALREADY_ROUTED', 'CONFIG_COPY_CLASHES', 'BRANCH_HAS_OPEN_TICKETS',
  'CONFIG_FOREIGN_ROW', 'UNKNOWN_MARK',
  // The installation owner's licence screen, English (Ruling 9).
  'LICENCE_LOCKED', 'LICENCE_FOR_ANOTHER_INSTALLATION', 'LICENCE_LIMIT_REACHED',
  // Free-form form sentences: translated only where the server sends a reason.
  'VALIDATION_FAILED',
  // Part B: the customer's phone page reads kioskText, and a field's count of
  // values is an administrator's sentence with a number in it.
  'FOLLOW_ENDED', 'FOLLOW_TOO_MANY', 'NO_SUCH_TICKET', 'TICKET_FINISHED', 'ALREADY_AT_THE_DESK', 'FIELD_HOLDS_VALUES',
  // Part G: the follow page's text box, also read from kioskText.
  'NOT_A_PHONE', 'NUMBER_NOT_TEXTED', 'TEXT_NOT_OFFERED',
  // Part E: the sentence varies with what the directory answered.
  'DIRECTORY_NOT_MAPPED',
  // Part E, Ruling 8: the installation owner's directory screens — testing a
  // directory (DIRECTORY_REFUSED), and the administrator's own storage refusing
  // to keep the bind password (SECRETS_UNAVAILABLE) — plus the People screen's
  // hand-over refusal when no directory is switched on to hand an account to
  // (NO_DIRECTORY), which is a second, differently-worded use of that same code
  // from the directory-test screen: a code with two English sentences behind it
  // is a reason to leave it in English, not a reason to pick one of the two.
  'DIRECTORY_REFUSED', 'NO_DIRECTORY', 'SECRETS_UNAVAILABLE',
  // The booking listener's own refusals, worded by the server in the caller's
  // language (internal/booking/refusals.go); no staff screen meets them.
  'BOOKING_KEY', 'KEY_NOT_TRUSTED', 'RATE_LIMITED', 'RESEND_LIMIT', 'HOLD_EXPIRED', 'ALREADY_CONFIRMED', 'TOO_MANY_HOLDS',
  // The data feed's file routes, answered to a bank's BI tool, never a person.
  'FEED_KEY',
  // The Mail server section, which only the installation owner uses (part J, Ruling 1).
  'MAIL_REFUSED', 'MAIL_UNREACHABLE', 'MAIL_MISCONFIGURED', 'NO_MAIL_SERVER',
  // P10b part B, screen apps. An upload's refusal lists every fault in the zip and
  // the manifest, which only the server can word; the others name an app, a
  // version or a count, and are read by the installation owner or the person
  // placing an app. NO_SUCH_FILE is answered to an app's own frame, not a person.
  'APP_REFUSED', 'APP_TOO_LARGE', 'APP_CONFLICT', 'APP_RETIRED', 'APP_PLACED', 'APP_KIND_MISMATCH',
  'BAD_ATTRIBUTES', 'NO_SUCH_FILE',
  // An app's values refused at the kiosk: answered to the app through the host
  // (web/src/host), which hands the app the reason, never a person.
  'FIELDS_REFUSED',
  // P10b part C, qms.call: answered to an app through the host, which hands it the
  // code as the refusal's reason (web/src/lib/api.ts, screenCall), never to a
  // person. CONNECTOR_SECRET_UNREADABLE is a fault of the server's, and reaches
  // the app as UNAVAILABLE.
  'CONNECTOR_NOT_DECLARED', 'NO_SUCH_CONNECTOR', 'CONNECTOR_OFF', 'TOO_MANY_CALLS', 'BAD_PARAMS',
  'METHOD_NOT_ALLOWED', 'CONNECTOR_TIMEOUT', 'CONNECTOR_UNREACHABLE', 'CONNECTOR_TOO_LARGE', 'PATH_NOT_ALLOWED',
  'CONNECTOR_SECRET_UNREADABLE',
  // An organisation leaving the service: only the installation owner offboards,
  // exports and purges (internal/api/offboard.go).
  'ALREADY_OFFBOARDED', 'NOT_OFFBOARDED', 'ROW_SECURITY_NOT_BINDING',
]
